import { useTranslation } from 'react-i18next';
import { Box, Chip, Tooltip, alpha, Theme } from '@mui/material';
import { useSystemHealth } from '../../../hooks/useSystemHealth';
import { useGetAuthUser } from '../../../hooks/useGetAuthUser';

// ----------------------------------------------------------------------

type Tone = 'success' | 'error' | 'warning' | 'default';

export default function PosStatusChip() {
  const { t } = useTranslation();
  const { user } = useGetAuthUser();
  const { pos, reachable } = useSystemHealth(60);

  // Le statut POS dans le header est masqué pour le super admin (il voit déjà tout dans le menu).
  const role = String(user?.role || '').toUpperCase();
  const isSuperAdmin =
    role.includes('SUPER_ADMIN') || role === '0' || user?.email === 'admin@ailyn.io';
  if (isSuperAdmin) {
    return null;
  }

  let tone: Tone = 'default';
  let labelKey = 'nav.health.pos_not_configured';

  if (!reachable) {
    tone = 'default';
    labelKey = 'nav.health.pos_unknown';
  } else if (pos.status === 'CONNECTED') {
    tone = 'success';
    labelKey = 'nav.health.pos_connected';
  } else if (pos.status === 'ERROR') {
    tone = 'error';
    labelKey = 'nav.health.pos_error';
  } else if (pos.status === 'SYNCING') {
    tone = 'warning';
    labelKey = 'nav.health.pos_syncing';
  }

  const baseColor = (theme: Theme) => {
    if (tone === 'success') return theme.palette.success.main;
    if (tone === 'error') return theme.palette.error.main;
    if (tone === 'warning') return theme.palette.warning.main;
    return theme.palette.grey[500];
  };

  const label = `${String(t(labelKey))}${pos.type ? ` · ${pos.type}` : ''}`;

  return (
    <Tooltip title={pos.type ? `POS: ${pos.type}` : String(t('nav.health.pos_not_configured'))}>
      <Chip
        size="small"
        variant="outlined"
        icon={
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              ml: 1,
              borderRadius: '50%',
              bgcolor: (theme) => baseColor(theme),
              boxShadow: (theme) => `0 0 0 3px ${alpha(baseColor(theme), 0.16)}`,
            }}
          />
        }
        label={label}
        sx={{
          fontWeight: 600,
          borderColor: (theme) => alpha(baseColor(theme), 0.32),
          color: 'text.secondary',
          '& .MuiChip-icon': { mr: 0.5 },
        }}
      />
    </Tooltip>
  );
}